import { useState } from 'react';

import { Button } from '@/components/ui/button';

import { TemplateCodeEditor } from '../TemplateCodeEditor';
import { VariableAutocomplete } from '../VariableAutocomplete';
import { VariablePicker } from '../VariablePicker';

import type {
  AnalyticsNodeMeta,
  AgentNodeMeta,
  ClassifierNodeMeta,
  QualityNodeMeta,
  ReviewNodeMeta,
} from '../VariablePicker';
import type { NotificationNodeData } from '@repo/shared-types';

interface NotificationConfigProps {
  data: NotificationNodeData;
  onChange: (data: NotificationNodeData) => void;
  responseFieldsSchema?: Record<string, unknown>;
  reviewNodeMetas?: ReviewNodeMeta[];
  classifierNodeMetas?: ClassifierNodeMeta[];
  qualityNodeMetas?: QualityNodeMeta[];
  agentNodeMetas?: AgentNodeMeta[];
  analyticsNodeMetas?: AnalyticsNodeMeta[];
  triggerType?: 'call' | 'dealerkit' | 'any' | 'scheduled' | 'webhook' | 'event';
}

export const NotificationConfig = ({
  data,
  onChange,
  responseFieldsSchema,
  reviewNodeMetas,
  classifierNodeMetas,
  qualityNodeMetas,
  agentNodeMetas,
  analyticsNodeMetas,
  triggerType,
}: NotificationConfigProps) => {
  const [editorOpen, setEditorOpen] = useState(false);

  const update = (patch: Partial<NotificationNodeData>) => onChange({ ...data, ...patch });

  const pickerProps = {
    responseFieldsSchema,
    reviewNodeMetas,
    classifierNodeMetas,
    qualityNodeMetas,
    agentNodeMetas,
    analyticsNodeMetas,
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Label</label>
        <VariableAutocomplete
          value={data.label}
          onChange={(v) => update({ label: v })}
          placeholder="Node label"
          {...pickerProps}
        />
      </div>

      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Title</label>
        <div className="flex gap-1">
          <VariableAutocomplete
            value={data.title ?? ''}
            onChange={(v) => update({ title: v })}
            placeholder="e.g. High-intent lead: {{contact.name}}"
            className="flex-1"
            {...pickerProps}
          />
          <VariablePicker
            onSelect={(v) => update({ title: (data.title ?? '') + v })}
            triggerType={triggerType}
            {...pickerProps}
          />
        </div>
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <label className="text-muted-foreground text-xs font-medium">Body</label>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => setEditorOpen(true)}
          >
            Edit Template
          </Button>
        </div>
        {data.body ? (
          <div
            className="border-input bg-muted/30 max-h-32 cursor-pointer overflow-hidden rounded-md border p-2 text-xs"
            onClick={() => setEditorOpen(true)}
          >
            <p className="text-muted-foreground line-clamp-5 font-mono">{data.body}</p>
          </div>
        ) : (
          <div
            className="border-input text-muted-foreground cursor-pointer rounded-md border border-dashed p-4 text-center text-xs"
            onClick={() => setEditorOpen(true)}
          >
            Click to edit notification body...
          </div>
        )}
      </div>

      <TemplateCodeEditor
        value={data.body ?? ''}
        onChange={(v) => update({ body: v })}
        type="sms"
        reviewSchema={responseFieldsSchema}
        reviewNodeMetas={reviewNodeMetas}
        classifierNodeMetas={classifierNodeMetas}
        qualityNodeMetas={qualityNodeMetas}
        agentNodeMetas={agentNodeMetas}
        analyticsNodeMetas={analyticsNodeMetas}
        open={editorOpen}
        onOpenChange={setEditorOpen}
      />

      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Recipients (optional)</label>
        <div className="flex gap-1">
          <VariableAutocomplete
            value={data.to ?? ''}
            onChange={(v) => update({ to: v })}
            placeholder="ops@example.com, {{config.notify_email}}"
            className="flex-1"
            {...pickerProps}
          />
          <VariablePicker
            onSelect={(v) => update({ to: (data.to ?? '') + v })}
            triggerType={triggerType}
            {...pickerProps}
          />
        </div>
        <p className="text-muted-foreground text-[10px]">
          Comma-separated user emails. Leave blank to notify every user with extension notifications
          enabled.
        </p>
      </div>
    </div>
  );
};
